/**
 * Cliente fetch que injeta X-Api-Key + X-User-Id em todas as chamadas
 * pra /api/v1/*. Lê credenciais do helper auth.
 *
 * Em desenvolvimento (vite dev) o proxy do vite redireciona /api -> backend.
 * Em prod, a UI sobe junto com a api atras do mesmo dominio (Traefik), entao
 * `/api/...` ja vai pro container correto.
 */
import { getCredenciais, clearCredenciais } from './auth';

const API_BASE = '/api/v1';

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  query?: Record<string, string | number | undefined>;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, mensagem: string) {
    super(mensagem);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(endpoint: string, opts: ApiOptions = {}): Promise<T> {
  const cred = getCredenciais();
  if (!cred) {
    throw new ApiError(401, null, 'Sem credenciais — configure em /credenciais');
  }

  const { method = 'GET', body, headers = {}, query } = opts;

  const url = new URL(API_BASE + endpoint, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
    }
  }

  const init: RequestInit = {
    method,
    headers: {
      'X-Api-Key': cred.apiKey,
      'X-User-Id': cred.userId,
      ...(cred.userEmail ? { 'X-User-Email': cred.userEmail } : {}),
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
  };
  if (body) init.body = JSON.stringify(body);

  const r = await fetch(url.toString().replace(window.location.origin, ''), init);
  if (r.status === 401) {
    clearCredenciais();
    window.dispatchEvent(new CustomEvent('filmaker:auth-invalida'));
    throw new ApiError(401, null, 'Credenciais invalidas — faca login novamente');
  }

  if (r.status === 204) return undefined as T;

  const text = await r.text();
  let parsed: unknown;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }

  if (!r.ok) {
    const msg =
      typeof parsed === 'object' && parsed && 'error' in parsed
        ? typeof (parsed as any).error === 'string'
          ? (parsed as any).error
          : JSON.stringify((parsed as any).error)
        : `HTTP ${r.status}`;
    throw new ApiError(r.status, parsed, msg);
  }

  return parsed as T;
}

export const api = {
  get: <T>(endpoint: string, query?: ApiOptions['query']) => request<T>(endpoint, { query }),
  post: <T>(endpoint: string, body: unknown) => request<T>(endpoint, { method: 'POST', body }),
  put: <T>(endpoint: string, body: unknown) => request<T>(endpoint, { method: 'PUT', body }),
  patch: <T>(endpoint: string, body: unknown) => request<T>(endpoint, { method: 'PATCH', body }),
  delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
};
